(function () {
  'use strict';

  class Lightbox {
    constructor(root) {
      this.root = root || document;
      this.DOM = undefined;
      this.isMediaModal = false;
      this.moved = false;
      this.openedBy = null;
      this._focusTrapActive = false;
      this._backgroundNodes = [];
      this._sectionId = null;

      this.onOpen = (event) => {
        event.preventDefault();
        const opener = event.currentTarget;
        this.show(opener);
      };

      this.onClose = (event) => {
        event.preventDefault();
        this.hide();
      };

      this.onKeyUp = (event) => {
        if (event.code && event.code.toUpperCase() === 'ESCAPE') this.hide();
      };

      this.onKeyDown = (event) => {
        if (event.defaultPrevented) return;
        const isEscape = event.key === 'Escape' || (event.code && event.code.toUpperCase() === 'ESCAPE');
        if (!isEscape) return;
        if (!this.DOM || !this.DOM.modal || !this.DOM.modal.hasAttribute('open')) return;
        event.preventDefault();
        this.hide();
      };

      this.onPointerUp = (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      };

      this.onBackgroundClick = (event) => {
        if (event.target === this.DOM.modal) this.hide();
      };

      this.handleTabTrap = (e) => {
        if (!this._focusTrapActive) return;
        if (e.key !== 'Tab') return;
        const focusables = this.getFocusableElements(this.DOM.modal);
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      };

      this.init();
    }

    init() {
      const modal = this.root.querySelector('.js-product-modal');
      if (!modal) return;

      this.DOM = {};
      this.DOM.modal = modal;
      this.DOM.openers = this.root.querySelectorAll('.js-product-modal-opener');
      this.DOM.dialog = this.DOM.modal.querySelector('[role="dialog"]');
      this.DOM.document = this.DOM.modal.querySelector('[role="document"]');
      this.DOM.closeButtons = this.DOM.modal.querySelectorAll('.js-product-modal-close, [id^="ModalClose-"]');

      this.isMediaModal = this.DOM.modal.classList.contains('media-modal');
      this.moved = false;
      this.openedBy = null;
      this._focusTrapActive = false;
      this._backgroundNodes = [];

      const section = this.DOM.modal.closest('.shopify-section');
      if (section && section.id) {
        this._sectionId = section.id.replace('shopify-section-', '');
      }

      this.setup();
    }

    setup() {
      if (this.DOM.openers && this.DOM.openers.length) {
        for (let i = 0; i < this.DOM.openers.length; i++) {
          this.DOM.openers[i].addEventListener('click', this.onOpen);
        }
      }

      for (let i = 0; i < this.DOM.closeButtons.length; i++) {
        this.DOM.closeButtons[i].addEventListener('click', this.onClose);
      }

      this.DOM.modal.addEventListener('keyup', this.onKeyUp);

      if (this.isMediaModal) {
        this.DOM.modal.addEventListener('pointerup', this.onPointerUp);
      } else {
        this.DOM.modal.addEventListener('click', this.onBackgroundClick);
      }
    }

    show(opener) {
      if (!this.moved) {
        this.moved = true;
        if (this._sectionId) {
          this.DOM.modal.dataset.section = this._sectionId;
        }
        if (this.DOM.modal.parentElement !== document.body) {
          document.body.appendChild(this.DOM.modal);
        }
      }

      this.openedBy = opener || this.openedBy;
      const popup = this.DOM.modal.querySelector('.template-popup');
      const colorsId = this.openedBy.dataset.colorsId;

      document.body.classList.add('overflow-hidden');
      this.DOM.modal.setAttribute('open', '');
      this.DOM.modal.classList.add('color-' + colorsId);

      if (popup && typeof popup.loadContent === 'function') popup.loadContent();

      if (typeof window !== 'undefined' && typeof window.pauseAllMedia === 'function') {
        window.pauseAllMedia();
      }

      this.showActiveMedia();

      this.enableModalA11y();

      document.addEventListener('keydown', this.onKeyDown);
    }

    hide() {
      document.body.classList.remove('overflow-hidden');
      document.body.dispatchEvent(new CustomEvent('modalClosed'));
      this.DOM.modal.removeAttribute('open');

      document.removeEventListener('keydown', this.onKeyDown);

      this.disableModalA11y();

      if (this.openedBy && typeof this.openedBy.focus === 'function') {
        this.openedBy.focus();
      }

      if (typeof window !== 'undefined' && typeof window.pauseAllMedia === 'function') {
        window.pauseAllMedia();
      }
    }

    showActiveMedia() {
      if (!this.openedBy) return;
      const openerMediaId = this.openedBy.getAttribute('data-media-id');
      if (!openerMediaId) return;

      this.DOM.modal.querySelectorAll('[data-media-id]:not([data-media-id="' + openerMediaId + '"])').forEach((element) => {
        element.classList.remove('active');
      });

      const activeMedia = this.DOM.modal.querySelector('[data-media-id="' + openerMediaId + '"]');
      if (!activeMedia) return;

      const activeMediaTemplate = activeMedia.querySelector('template');
      const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
      activeMedia.classList.add('active');
      activeMedia.scrollIntoView();

      if (this.DOM.document && activeMedia && typeof activeMedia.width === 'number') {
        this.DOM.document.scrollLeft = (activeMedia.width - this.DOM.document.clientWidth) / 2;
      }

      if (
        activeMedia.nodeName === 'DEFERRED-MEDIA' &&
        activeMediaContent &&
        activeMediaContent.querySelector('.js-youtube')
      ) {
        if (typeof activeMedia.loadContent === 'function') activeMedia.loadContent();
      }
    }

    destroy() {
      if (!this.DOM) return;

      if (this.DOM.openers && this.DOM.openers.length) {
        for (let i = 0; i < this.DOM.openers.length; i++) {
          this.DOM.openers[i].removeEventListener('click', this.onOpen);
        }
      }

      for (let i = 0; i < this.DOM.closeButtons.length; i++) {
        this.DOM.closeButtons[i].removeEventListener('click', this.onClose);
      }

      this.DOM.modal.removeEventListener('keyup', this.onKeyUp);
      document.removeEventListener('keydown', this.onKeyDown);
      if (this.isMediaModal) {
        this.DOM.modal.removeEventListener('pointerup', this.onPointerUp);
      } else {
        this.DOM.modal.removeEventListener('click', this.onBackgroundClick);
      }

      this.disableModalA11y();

      this.DOM = undefined;
    }

    getFocusableElements(root) {
      if (!root) return [];
      const selectors = [
        'a[href]',
        'area[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'iframe',
        'object',
        'embed',
        '[contenteditable]',
        '[tabindex]:not([tabindex="-1"])',
      ];
      return Array.from(root.querySelectorAll(selectors.join(','))).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
    }

    focusFirstInModal() {
      const focusables = this.getFocusableElements(this.DOM.modal);
      const closeBtn = this.DOM.modal.querySelector('.js-product-modal-close, [id^="ModalClose-"]');
      const target = closeBtn || this.DOM.dialog || this.DOM.document || focusables[0];
      if (target && typeof target.focus === 'function') {
        target.focus();
      }
    }

    setBackgroundInert(enable) {
      const bodyChildren = Array.from(document.body.children);
      this._backgroundNodes = bodyChildren.filter((node) => node !== this.DOM.modal);

      const supportsInert = 'inert' in HTMLElement.prototype;
      this._backgroundNodes.forEach((node) => {
        if (enable) {
          if (supportsInert) {
            node.inert = true;
          } else {
            node.setAttribute('aria-hidden', 'true');
          }
        } else {
          if (supportsInert) {
            node.inert = false;
          } else {
            node.removeAttribute('aria-hidden');
          }
        }
      });
    }

    enableModalA11y() {
      this.setBackgroundInert(true);
      this._focusTrapActive = true;
      this.focusFirstInModal();
    }

    disableModalA11y() {
      this.setBackgroundInert(false);
      this._focusTrapActive = false;
    }
  }

  window.Universe.Lightbox = Lightbox;
})();
